import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, UserPlus, X } from 'lucide-react';
import { api } from '../../api/client';
import { unwrapArray } from '../../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS, queryLoadFailure } from '../../lib/load-failure';
import { CustomerStatusBadge } from '../customers/CustomerStatusBadge';
import { QuickAddProspect } from '../customers/QuickAddProspect';
import { useCustomerPermissions } from '../../hooks/useCustomerPermissions';

export interface PickedCustomer {
  id: string;
  name: string;
  status?: string | null;
}

/**
 * Customer typeahead over GET /customers — ALL statuses, because a meeting is
 * as often with a prospect or a lapsed client as with an active one — plus
 * "+ New prospect" (CRM-FE-1's quick-add) for a company not on file yet.
 *
 * A failed customer list is said so; it is not "no matches".
 */
export function CustomerTypeahead({
  value,
  onChange,
  disabled,
}: {
  value: PickedCustomer | null;
  onChange: (c: PickedCustomer | null) => void;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [quickAdd, setQuickAdd] = useState(false);
  const { canCreateProspect } = useCustomerPermissions();

  const customersQuery = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.customers.list().then((r) => unwrapArray<any>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(customersQuery);
  const customers = useMemo(() => (customersQuery.data ?? []) as any[], [customersQuery.data]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? customers.filter((c) => (c.name || '').toLowerCase().includes(q)) : customers;
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || '')).slice(0, 8);
  }, [customers, query]);

  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 py-2.5">
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-white">{value.name}</span>
        <CustomerStatusBadge status={value.status} />
        {!disabled && (
          <button type="button" onClick={() => onChange(null)} aria-label="Change customer" className="text-fieldiq-muted hover:text-white">
            <X size={16} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
        <input
          id="meeting-customer"
          role="combobox"
          aria-expanded={open}
          aria-controls="meeting-customer-options"
          aria-autocomplete="list"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search customers…"
          autoComplete="off"
          className="w-full rounded-lg border border-fieldiq-border bg-fieldiq-dark py-2.5 pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
        />
      </div>
      {open && (
        <div
          id="meeting-customer-options"
          role="listbox"
          aria-label="Customers"
          className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border border-fieldiq-border bg-fieldiq-card shadow-xl"
        >
          {failure ? (
            <p role="alert" className="px-3 py-3 text-sm text-fieldiq-text">
              {failure === 'refused' ? "You don't have access to the customer list." : "Couldn't load customers."}
            </p>
          ) : customersQuery.data === undefined ? (
            <p className="px-3 py-3 text-sm text-fieldiq-muted">Loading customers…</p>
          ) : matches.length === 0 ? (
            <p className="px-3 py-3 text-sm text-fieldiq-muted">No customer matches “{query.trim()}”.</p>
          ) : (
            matches.map((c) => (
              <button
                key={c.id}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => { onChange({ id: c.id, name: c.name, status: c.status }); setOpen(false); setQuery(''); }}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-white hover:bg-fieldiq-dark"
              >
                <span className="min-w-0 flex-1 truncate">{c.name}</span>
                <CustomerStatusBadge status={c.status} />
              </button>
            ))
          )}
          {canCreateProspect && (
            <button
              type="button"
              onClick={() => { setQuickAdd(true); setOpen(false); }}
              className="flex w-full items-center gap-2 border-t border-fieldiq-border px-3 py-2.5 text-left text-sm font-semibold text-fieldiq-accent-signal hover:bg-fieldiq-dark"
            >
              <UserPlus size={14} aria-hidden="true" /> New prospect
            </button>
          )}
        </div>
      )}
      <QuickAddProspect
        open={quickAdd}
        initialName={query.trim()}
        onClose={() => setQuickAdd(false)}
        onCreated={(c) => { setQuickAdd(false); setQuery(''); onChange({ id: c.id, name: c.name, status: c.status ?? 'prospect' }); }}
        onUseExisting={(id) => {
          setQuickAdd(false);
          const existing = customers.find((c) => c.id === id);
          onChange({ id, name: existing?.name ?? query.trim(), status: existing?.status ?? null });
        }}
      />
    </div>
  );
}

export default CustomerTypeahead;
